import type { Lead } from '../types';
import { parseLeadTags, STATUS_COLOR, STATUS_LABEL } from '../types';
import { hasVerifiedPayment, isMapConsultationLead, isMapLead } from '../lib/lead-context';
import { leadContactPolicy } from '../../../shared/contact-policy';

const TIER_META: Record<1 | 2 | 3, { label: string; badge: string; dot: string }> = {
  1: { label: 'HOT', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  2: { label: 'WARM', badge: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-400' },
  3: { label: 'COLD', badge: 'bg-slate-100 text-slate-500 border-slate-200', dot: 'bg-slate-400' },
};

const EXTRA_HOT_BADGE = {
  label: 'EXTRA HOT',
  badge: 'bg-gradient-to-r from-red-500 to-orange-500 text-white border-red-600',
  dot: 'bg-red-600',
};

const SOURCE_META: Record<string, { label: string; icon: string; badge: string }> = {
  social: {
    label: 'Social',
    icon: '💬',
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200 font-medium'
  },
  whatsapp: { 
    label: 'WhatsApp', 
    icon: '📱', 
    badge: 'bg-green-100 text-green-800 border-green-200 font-medium' 
  },
  meta_form: { 
    label: 'Meta', 
    icon: '📘', 
    badge: 'bg-blue-100 text-blue-800 border-blue-200 font-medium' 
  },
  call: { 
    label: 'Call', 
    icon: '📞', 
    badge: 'bg-purple-100 text-purple-800 border-purple-200 font-medium' 
  },
  neodove: {
    label: 'NeoDove',
    icon: '☎️',
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200 font-medium'
  },
  feedback: {
    label: 'Feedback',
    icon: '💬',
    badge: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 font-medium'
  },
};

function sourceHistory(value: Lead['source_history']): string[] {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function sourceLabel(source: string) {
  return SOURCE_META[source]?.label || source.replace(/_/g, ' ');
}

function fmtTime(iso: string) {
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

export default function LeadCard({
  lead,
  onSelect,
  showStatus = false,
  me,
  selected = false,
  action,
  compact = false,
  whatsappActivity = false,
}: {
  lead: Lead;
  onSelect: (id: string) => void;
  showStatus?: boolean;
  me?: string;
  selected?: boolean;
  compact?: boolean;
  whatsappActivity?: boolean;
  action?: {
    label: string;
    onClick: (id: string) => void;
    tone?: 'primary' | 'secondary';
    disabled?: boolean;
  };
}) {
  const meta = TIER_META[lead.tier_score];
  const contactPolicy = leadContactPolicy(lead);
  const isExtraHot = parseLeadTags(lead.tags).includes('extra_hot');
  const tierDisplay = !contactPolicy.messageAllowed
    ? { label: 'REJECTED', badge: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' }
    : isExtraHot
    ? { label: EXTRA_HOT_BADGE.label, badge: EXTRA_HOT_BADGE.badge, dot: EXTRA_HOT_BADGE.dot }
    : meta;
  const history = sourceHistory(lead.source_history);
  const latestSource = lead.latest_source?.trim() || history.at(-1) || lead.source;
  const sourceMeta = SOURCE_META[latestSource] || SOURCE_META[lead.source];
  const sourceCount = history.length;
  const mine = me && lead.assigned_to === me;
  const assignedLabel = lead.assigned_to
    ? mine
      ? 'You'
      : lead.assigned_to.split('@')[0]
    : null;
  const lastAssignedLabel = lead.last_assigned_to
    ? lead.last_assigned_to === lead.assigned_to
      ? null
      : lead.last_assigned_to.split('@')[0]
    : null;

  return (
    <div
      className={`block w-full overflow-hidden border bg-white text-left active:scale-[0.995] ${
        compact ? 'rounded-xl shadow-none' : 'rounded-2xl shadow-sm'
      } ${
        selected ? 'border-wechat-accent ring-2 ring-wechat-accent/20' : 'border-slate-200'
      }`}
    >
      <button type="button" onClick={() => onSelect(lead.id)} className="block w-full text-left">
        <div className={`flex items-start ${compact ? 'gap-2 px-3 pt-2.5' : 'gap-3 px-4 pt-3'}`}>
          <div className={`${compact ? 'mt-1.5 h-2 w-2' : 'mt-1 h-2.5 w-2.5'} flex-none rounded-full ${tierDisplay.dot}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className={`flex min-w-0 flex-1 flex-wrap items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
                {sourceMeta && (
                  <span
                    title="Latest source"
                    className={`rounded-full border px-2 text-[10px] font-bold ${compact ? 'py-0.5' : 'py-1'} ${sourceMeta.badge}`}
                  >
                    {!compact && `${sourceMeta.icon} `}{sourceLabel(latestSource)}
                  </span>
                )}
                {isMapLead(lead) && (
                  <span className="rounded-full border border-teal-200 bg-teal-50 px-2 py-1 text-[10px] font-bold text-teal-800">Map</span>
                )}
                {isMapConsultationLead(lead) && (
                  <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-bold text-sky-800">Consultation Requested</span>
                )}
                {lead.is_blocked && (
                  <span className="rounded-full border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-bold text-red-700">Blocked</span>
                )}
                {hasVerifiedPayment(lead) && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-800">
                    ₹{(Number(lead.verified_payment_amount_minor) / 100).toLocaleString('en-IN')} Paid
                  </span>
                )}
                {sourceCount > 1 && (
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600">
                    +{sourceCount - 1} sources
                  </span>
                )}
                {!!lead.returned_lead && (
                  <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-800">
                    Returned Lead
                  </span>
                )}
                <h2 className={`${compact ? 'text-[15px]' : 'text-base'} min-w-0 flex-1 truncate font-semibold text-slate-900`}>{lead.name}</h2>
              </div>
              <span
                className={`flex-none rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${tierDisplay.badge}`}
              >
                {tierDisplay.label}
              </span>
            </div>
            <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-slate-500 ${compact ? 'mt-0.5 text-[11px]' : 'text-xs'}`}>
              <span>{lead.phone}</span>
              <span>·</span>
              <span>{fmtTime(lead.created_at)}</span>
              {assignedLabel && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    mine ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  {mine ? 'You' : `→ ${assignedLabel}`}
                </span>
              )}
              {!assignedLabel && lastAssignedLabel && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                  Previously: {lastAssignedLabel}
                </span>
              )}
              {whatsappActivity && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  New WhatsApp
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={compact
          ? 'mx-3 mt-2 line-clamp-2 border-t border-slate-100 pt-2 text-xs leading-4 text-slate-600'
          : 'mx-4 mt-3 rounded-2xl rounded-tl-sm bg-wechat-bubble px-3 py-2 text-sm leading-5 text-slate-900'}>
          {contactPolicy.guidance || lead.opening_line}
        </div>

        <div className={`flex items-center justify-between text-xs ${compact ? 'mx-3 mb-2 mt-1.5 py-1' : 'px-4 pb-3 pt-2'}`}>
          <span className="line-clamp-1 italic text-slate-500">{!contactPolicy.messageAllowed ? 'Rejected · Contact blocked' : contactPolicy.guidance ? 'Contact preference recorded' : lead.persuasion_tip}</span>
          {showStatus && (
            <span
              className={`ml-2 flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[lead.status]}`}
            >
              {STATUS_LABEL[lead.status]}
            </span>
          )}
        </div>
      </button>

      {action && (
        <div className="px-4 pb-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(lead.id);
            }}
            disabled={action.disabled}
            className={`w-full rounded-xl px-3 py-2 text-sm font-semibold ${
              action.tone === 'secondary'
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {action.label}
          </button>
        </div>
      )}
    </div>
  );
}
